/** Assets modelled with their own stochastic process. */
export const RISKY_ASSETS = ['equity', 'debt', 'gold'] as const
export type RiskyAsset = (typeof RISKY_ASSETS)[number]

/**
 * Cash is modelled deterministically rather than as a fourth correlated asset.
 * Liquid-fund volatility at a monthly horizon is a rounding error next to
 * equity's, and dropping it shrinks the Cholesky to 3x3 and cuts a quarter of
 * the normal draws — which is a quarter of the simulation's whole cost.
 */
export type AssetKey = RiskyAsset | 'cash'

/** Portfolio weights. Must sum to 1. */
export type Allocation = Record<AssetKey, number>

export interface AssetProcess {
  /** Annual nominal expected return, as a decimal (0.12 = 12%). */
  mean: number
  /** Annual standard deviation of returns, as a decimal. */
  vol: number
}

export interface InflationProcess {
  /** Long-run mean annual inflation. */
  mean: number
  /** Unconditional standard deviation of the annual rate. */
  vol: number
  /** AR(1) persistence. 0 = white noise year to year, 1 = random walk. */
  phi: number
  /** Rate in force for the first year. */
  initial: number
}

export interface MarketModel {
  assets: Record<RiskyAsset, AssetProcess>
  /** Annual nominal return on the cash sleeve. Deterministic. */
  cashReturn: number
  /**
   * Correlation matrix over RISKY_ASSETS, in that exact order.
   * Must be symmetric with a unit diagonal and positive semi-definite.
   */
  correlation: number[][]
  inflation: InflationProcess
}

export interface SimInputs {
  currentAge: number
  /** The age the headline probability is quoted against. */
  fireByAge: number
  /** How far the projection runs. Held constant while fireByAge is dragged so
   *  the chart's x-axis doesn't jump under the user's thumb. */
  projectToAge: number
  /** Existing invested corpus, in rupees. */
  currentCorpus: number
  /** Monthly contribution in rupees, at today's rate. */
  monthlyInvestment: number
  /** Nominal annual increase in the contribution, as a decimal (0.1 = 10% a year). */
  annualStepUpPct: number
  /** Annual spending the corpus must support, in today's rupees. */
  annualExpensesToday: number
  /** Corpus needed = inflated annual expenses x this. 30 ≈ a 3.33% withdrawal rate. */
  fireMultiple: number
  allocation: Allocation
}

export interface SimConfig {
  /** Number of Monte Carlo paths. */
  paths: number
  /** Fixing this makes a given set of inputs always produce the same chart,
   *  so a shared link reproduces exactly what its sender saw. */
  seed: number
}

export interface PercentileBands {
  p10: Float32Array
  p25: Float32Array
  p50: Float32Array
  p75: Float32Array
  p90: Float32Array
}

export interface SimResult {
  /** Age at each sample point, length horizonYears + 1. */
  ages: Float32Array
  /** Corpus percentiles at each age, in nominal rupees. */
  bands: PercentileBands
  /** Median corpus required to declare FIRE at each age. Stochastic inflation
   *  makes this a distribution too, so the chart draws its median. */
  requiredMedian: Float32Array
  /** Fraction of paths whose corpus covers their own inflated requirement, by age. */
  successByAge: Float32Array
  /**
   * Index into `ages` for fireByAge. Exposed because the chart scales its y-axis
   * to the decision point rather than to the tallest thing in the projection.
   */
  targetIndex: number
  /** The headline: P(FIRE) at fireByAge. */
  probabilityAtTarget: number
  /** Median corpus at fireByAge, nominal rupees. */
  medianCorpusAtTarget: number
  /** Median required corpus at fireByAge, nominal rupees. */
  requiredAtTarget: number
  /** Earliest age where at least half of paths have made it. Null if never. */
  medianFireAge: number | null
  /** Earliest age where 80% of paths have made it. Null if never. */
  confidentFireAge: number | null
  /** Echoed back so the UI can tell a 2k preview from a 10k final run. */
  paths: number
}
